import moment from 'moment';
import 'moment-timezone';
import * as _ from 'lodash';

import Stats from 'stats.js';
import {OrderDetailsContainer, OrderCardsContainer, OrderMarksContainer, OrderDetailsLine} from './containers/orders';
import {AxisMarks, Axis} from './containers/axis'
import {MousePosition} from "./containers/mouse";
import {CurrentValue, Chart} from "./containers/chart";
import {Title, HoverMaskTrade} from './containers/other';

import {Animation, Animations, newPointAnimation, yScaleAnimation, zoomAnimation} from './animations';
import {scales, colors, Point, updateLayersOrder, getTz} from "./utils";


export default class ChartApp{
  constructor(quotes, trades, account, feed){
    moment.tz.setDefault(getTz().tz);
    let widgetDomElement = document.getElementById('tv-widget');
    this.quotes = quotes;
    this.account = account;
    this.trades = trades;
    this.feed = feed;
    this.trades.hoverTypeTrade = false;

    this.devicePixelRatio = window.devicePixelRatio;
    let width = widgetDomElement.offsetWidth - 20;
    let height = document.getElementsByClassName('chart')[0].offsetHeight;
    let optionsRender = {
      transparent: true,
      resolution:this.devicePixelRatio
    };

    this.modalStatus = false;

    PIXI.utils.skipHello();
    this.renderer = new PIXI.CanvasRenderer(width, height, optionsRender);

    this.points = [];
    this.scale = 1;  // in minutes
    this.lastMousePosition = {x: 0, y: 0};
    this.startTimestamp = Date.now() - 1000 * 60 * 6;
    this.endTimestamp = Date.now() + 1000 * 60 * 2;
    this.rightMargin = 75;
    this.initTime = Date.now();


    this.renderer.view.style.backgroundColor = 'black';
    this.renderer.view.style.width = `${width}px`;
    this.renderer.view.style.height = `${height}px`;

    this.initContainers();
    addWheelListener(this.renderer.view, this.mousewheelHandler.bind(this));

    this.animations = new Animations();
    this.lastNow = Date.now();

    if (IS_DEBUG) {
      this.stats = new Stats();
      this.stats.domElement.style.top = '40px';
      this.stats.domElement.style.left = '380px';
      widgetDomElement.appendChild(this.stats.domElement);
    }

    widgetDomElement.appendChild(this.renderer.view);

    this.getQuotes(this.startTimestamp, this.initTime).always(() => {
      this.startAnimation();
      this.initNewPointInterval();
    });
  }

  initContainers(){
    // creating containers and other stuff
    this.stage = new PIXI.Container();


    let landscapeTexture = PIXI.Texture.fromImage('/static/img/webtrader/bg.jpg');
    let width = parseInt(this.renderer.view.style.width, 10);
    let height = parseInt(this.renderer.view.style.height, 10);
    let texture2 = new PIXI.Texture(landscapeTexture, new PIXI.Rectangle(0, 0, width, height));

    let background = new PIXI.Sprite(texture2);
    background.anchor.x = 0;
    background.anchor.y = 0;

    background.position.x = 0;
    background.position.y = 0;

    this.stage.addChild( background );

    this.chartPosition = {
      top: this.renderer.height / this.devicePixelRatio * 0.2,
      height: this.renderer.height / this.devicePixelRatio * 0.6
    };

    this.orderMarks = new OrderMarksContainer(this);
    this.orderDetails = new OrderCardsContainer(this);
    this.orderDetailsCard = new OrderDetailsContainer(this);

    this.orderDetailsLine = new OrderDetailsLine(this);

    this.chart = new Chart(this);

    this.title = new Title(this); // background title of chart

    //noinspection JSCheckFunctionSignatures
    this.stage.addChild(this.title, new Axis(this), this.chart, new AxisMarks(this),  new MousePosition(this),
      this.orderMarks, this.orderDetailsLine, this.orderDetailsCard, this.orderDetails, new CurrentValue(this), new HoverMaskTrade(this));

    updateLayersOrder(this.stage);
  }

  mousewheelHandler(event){
    event.preventDefault();
    if (this.animations.animations.filter(animation => zoomAnimation.name == animation.name).length == 0
      && ((this.endTimestamp - this.startTimestamp > 1000 * 120) || event.deltaY > 0 )
      && ((this.endTimestamp - this.startTimestamp < 1000 * 60 * 60 * 24 * 365 * 5) || event.deltaY < 0 )) {
      this.animations.animations.push(new Animation(zoomAnimation, 250, this, event));
      setTimeout(this.checkScale.bind(this), 300);
    }
  }

  getColorTrade() {
    let colorLine = colors.chart;
    if (this.trades.hoverTypeTrade === 0) {
      colorLine = colors.colorTradeUp;
    } else if (this.trades.hoverTypeTrade === 1) {
      colorLine = colors.colorTradeDown;
    }

    return colorLine;
  }

  isHoverTrade() {
    return this.trades.hoverTypeTrade === false ? this.trades.hoverTypeTrade : true;
  }

  isModalToggle() {
    let isToggle = false;
    if (this.trades.modalOpened !== this.modalStatus) {
      isToggle = true;
    };
    this.modalStatus = this.trades.modalOpened;
    return isToggle;
  }

  changeSymbol(){
    this.stopAnimation();
    this.deInitNewPointInterval();
    this.points = [];
    this.displayedPoints = [];
    this.initTime = Date.now();
    this.getQuotes(this.startTimestamp, this.endTimestamp).always(() => {
      this.startAnimation();
      this.initNewPointInterval();
      this.title.setNewTitle(this.quotes.currentSymbol);
    });
  }

  changeScale(scale){
    if (this.downloadingChartHistory) return;
    this.stopAnimation();
    this.deInitNewPointInterval();
    this.points = this.points.filter((p) => p.timestamp > this.initTime);
    this.displayedPoints = [];
    this.scale = scale;
    this.getQuotes(this.startTimestamp, this.initTime).always(() => {
      this.startAnimation();
      this.initNewPointInterval();
    });
  }

  checkScale(){
    for (let scale of scales){
      if (scale.interval > (this.endTimestamp - this.startTimestamp)){
        if (this.scale != scale.scale && !this.changingScale) {
          this.changingScale = true;
          this.changeScale(scale.scale);
          this.changingScale = false;
        }
        return;
      }
    }
  }

  newPoint(){
    if (!this.changingSymbol){
      let newPoint = new Point(Date.now() + 2000, this.quotes.currentQuote.bid);

      if (newPoint.timestamp > this.endTimestamp || this.isFrameSleeping()) {
        this.points.push(newPoint);
      }
      else{
        this.animations.animations.push(new Animation(newPointAnimation, 250, this, newPoint));
      }
    }
  }

  initNewPointInterval(){
    if (this.changingSymbol){
      this.changingSymbol = false;
    }

    let intervalFunction = () => {
      this.newPoint();
      this.newPointInterval = setTimeout(intervalFunction.bind(this), 1000);
    };

    intervalFunction();

    // this.newPointInterval = setInterval(this.newPoint.bind(this), 1000);
  }

  deInitNewPointInterval(){
    this.changingSymbol = true;
    clearInterval(this.newPointInterval);
  }

  initPoints(data){
    let points = [];
    let offSet = getTz().offset;
    for (let p of data){
      if (p.time - offSet * 60 * 1000 < Date.now()) {
        points.push(new Point(p.time - offSet * 60 * 1000, p.close))
      }
    }
    if (this.points.length == 0) {
      this.points = points;
      this.maxValue = _.maxBy(this.points, p => p.value).value;
      this.minValue = _.minBy(this.points, p => p.value).value;

      let p = new Point(Date.now() + 1000, _.last(this.points).value);
      this.points.push(p);

      let nowPoint = new Point(Date.now() + 1000, _.last(this.points).value);
      this.points.push(nowPoint);
    }
    else{
      points = points.filter((point) => point.timestamp < this.points[0].timestamp);
      this.points = points.filter((point) => point.timestamp < this.points[0].timestamp).concat(this.points);
    }
  }

  getX(timestamp){
    return (timestamp - this.startTimestamp) / (this.endTimestamp - this.startTimestamp) * this.chart._width;
  }

  getY(value){
    return this.chart._height * (1 - (value - this.minValue) / (this.maxValue - this.minValue));
  }

  getTimestamp(x){
    return this.startTimestamp + x / this.chart._width * (this.endTimestamp - this.startTimestamp);
  }

  getValue(y){
    return (this.chart._height + this.chart.position.y - y) / this.chart._height * (this.maxValue - this.minValue) + this.minValue
  }

  calculatePointCoordinates(point){
    point.x = this.getX(point.timestamp);
    point.y = this.getY(point.value);
  }

  recalculatePoints(points){
    for (let point of points){
      this.calculatePointCoordinates(point);
    }
  };

  recalculateStage(){
    // moves stage between frames
    if (this.animations.animations.filter(a => a.name == 'zoomAnimation').length == 0 && _.last(this.displayedPoints) == _.last(this.points)){
      this.endTimestamp += Date.now() - this.lastNow;
      this.startTimestamp += Date.now() - this.lastNow;
    }
    this.lastNow = Date.now();

    // loads history from left
    if (!this.downloadingChartHistory && this.points[0].timestamp - this.startTimestamp > 1000 * 15){
      this.getQuotes(this.startTimestamp - 1000 * 60 * 60, this.points[0].timestamp);
    }

    this.displayedPoints = this.points.filter((point) => point.timestamp >= this.startTimestamp && point.timestamp <= this.endTimestamp);
    if (this.displayedPoints.length > 0) {
      let startPointIndex = this.points.map(p => p.timestamp).indexOf(this.displayedPoints[0].timestamp);
      let endPointIndex = this.points.map(p => p.timestamp).indexOf(_.last(this.displayedPoints).timestamp);

      // adds extra points to fix edge of line problem
      if (startPointIndex > 0) {
        this.displayedPoints.unshift(this.points[startPointIndex - 1])
      }
      if (endPointIndex < this.points.length - 1) {
        this.displayedPoints.push(this.points[endPointIndex + 1])
      }

      this.recalculatePoints(this.displayedPoints);

      // calculating order points for OY chart scale
      let orders = [];
      if (this.trades.objects) {
        orders = this.trades.objects.filter((order) => order.symbol == this.quotes.currentSymbol &&
          order.options_data && order.options_data.expiration_time.unix() * 1000 > this.initTime && !order.isClosed &&
          _.inRange(order.options_data.expiration_time.unix() * 1000, this.startTimestamp, this.endTimestamp)
        );
      }

      let orderPoints = orders.map((order) => new Point(order.options_data.expiration_time.unix() * 1000, order.open_price));
      let newMax = _.maxBy(this.displayedPoints.concat(orderPoints), p => p.value).value;
      let newMin = _.minBy(this.displayedPoints.concat(orderPoints), p => p.value).value;

      if (newMax == newMin){
        newMax = _.maxBy(this.points, p => p.value).value;
        newMin = _.minBy(this.points, p => p.value).value;
      }

      if ((newMax != this.maxValue || newMin != this.minValue) &&
        this.animations.animations.filter(animation => yScaleAnimation.name == animation.name).length == 0) {
        this.animations.animations.push(new Animation(yScaleAnimation, 250, this, newMax, newMin));
      }

      this.orderDetailsCard.updatePosition();
      this.orderDetailsLine.updatePosition();
    }
  }

  startAnimation(){
    let animation = () => {
      if (IS_DEBUG) this.stats.begin();
      this.renderStage();
      if (IS_DEBUG) this.stats.end();
      this.frameID = requestAnimationFrame(animation);
    };

    requestAnimationFrame(animation);
  }

  stopAnimation(){
    if (this.frameID){
      window.cancelAnimationFrame(this.frameID);
      this.frameID = undefined;
    }
  }

  isFrameSleeping(){
    return Date.now() - this.renderedTime > 100;
  }

  renderStage(){
    this.renderedTime = Date.now();
    this.recalculateStage();
    this.animations.execute();

    for(let container of this.stage.children){
      if (container.render){
        container.render();
      }
    }

    this.renderer.render(this.stage);
  }

  getQuotes(from, to){
    this.downloadingChartHistory = true;
    if (to > this.initTime) to = this.initTime;
    return $.get({
      url: `${this.account.BASE_URL}/symbol_history/`,
      data: {symbol: this.quotes.currentSymbol, resolution: this.scale, from:  Math.floor(from / 1000),
        to: Math.floor(to / 1000)},
    }).done((data) => {
      this.initPoints(data);
      this.downloadingChartHistory = false;
      this.checkScale();
    })
  }
}
